// Página pública de aceite do convite da transportadora (PO1-2299):
// /public/convite-transportadora/:token. Mesma regra de senha e mesmo visual
// (glass-card) de views/pages/Register, mas fora de /auth: o Route.js manda
// usuário logado de /auth para o autopilot, e aqui existe Recusar.
// PO1-3646 (épico PO1-3359): convite da Rede Everlog vem com `preview.termo`
// (termo de adesão versionado); o aceite exige o checkbox nos dois ramos
// (conta nova e já tem acesso) e manda termoAceite/termoVersao.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Card, CardHeader, CardBody, CardFooter, Container, Col, Row, Input } from "reactstrap";
import { Link, useHistory, useLocation, useParams } from "react-router-dom";
import { comNext, lerNext } from "../../../utils/nextSeguro";

import { Form } from "@unform/web";
import * as Yup from "yup";
import logo from "../../../assets/img/autopilot-logo.png";
import getValidationErrors from "../../../utils/getValidationErrors";
import CustomInputGroupText from "components/CustomInputGroupText";
import { useToast } from "../../../hooks/toast";
import { previewConvite, aceitarConvite, recusarConvite } from "services/conviteTransportadoraPublicApi";
import "../../pages/Login/login.css";

function classesPresentes(senha) {
  let n = 0;
  if (/[a-z]/.test(senha)) n += 1;
  if (/[A-Z]/.test(senha)) n += 1;
  if (/[0-9]/.test(senha)) n += 1;
  if (/[^A-Za-z0-9]/.test(senha)) n += 1;
  return n;
}

const MENSAGEM_INDISPONIVEL = {
  EXPIRADO: "Este convite expirou. Peça ao embarcador para reenviar.",
  UTILIZADO: "Este convite já foi utilizado, cancelado ou substituído por um reenvio.",
  INVALIDO: "Convite inválido.",
};

const textoSuave = { color: "rgba(255,255,255,0.75)", textAlign: "center" };

// Termo de adesão à Rede Everlog (PO1-3646): título, preâmbulo, itens
// ({ titulo, texto }, molde do termo de certificados) e fecho, com o checkbox
// obrigatório. A versão vem do preview e volta no aceite.
function TermoRede({ termo, aceito, onChange, disabled }) {
  return (
    <div style={{ marginTop: 16, padding: "12px 14px", background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 8, fontSize: 13, color: "rgba(255,255,255,0.85)" }} data-testid="termo-rede">
      <div style={{ color: "#fff", fontWeight: 600, marginBottom: 6 }}>{termo.titulo || "Termo de adesão à Rede Everlog"}</div>
      {termo.preambulo ? <p style={{ marginBottom: 8 }}>{termo.preambulo}</p> : null}
      {(termo.itens || []).length ? (
        <ol style={{ paddingLeft: "1.2rem", marginBottom: 8 }}>
          {termo.itens.map((item, i) => {
            const obj = item && typeof item === "object" ? item : { titulo: "", texto: String(item || "") };
            return (
              <li key={i} style={{ marginBottom: 4 }}>
                {obj.titulo ? <strong style={{ color: "#fff" }}>{obj.titulo}. </strong> : null}
                {obj.texto}
              </li>
            );
          })}
        </ol>
      ) : null}
      {termo.fecho ? <p style={{ marginBottom: 8 }}>{termo.fecho}</p> : null}
      <label style={{ display: "flex", alignItems: "flex-start", gap: 8, cursor: "pointer", color: "#fff", marginBottom: 0 }}>
        <input type="checkbox" checked={Boolean(aceito)} onChange={(e) => onChange(e.target.checked)} disabled={disabled} style={{ marginTop: 3 }} data-testid="termo-aceite" />
        <span>
          Li e aceito o termo de adesão à Rede Everlog{termo.versao ? <span style={{ opacity: 0.6 }}> (versão {termo.versao})</span> : null}.
        </span>
      </label>
    </div>
  );
}

export default function ConviteTransportadora() {
  const { token } = useParams();
  const formRef = useRef(null);
  const history = useHistory();
  const { search } = useLocation();
  const { addToast } = useToast();
  // PO1-3391 (P3): o convite que veio do pedido de análise traz ?next= (o
  // Extrato do pedido); depois do aceite, o login leva a pessoa para lá.
  const next = lerNext(search);
  const rotaLogin = comNext("/auth/login", next);

  const [carregado, setCarregado] = useState(false);
  const [preview, setPreview] = useState(null);
  const [indisponivel, setIndisponivel] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [recusando, setRecusando] = useState(false);
  const [motivoRecusa, setMotivoRecusa] = useState("");
  const [recusado, setRecusado] = useState(false);
  const [termoAceito, setTermoAceito] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const p = await previewConvite(token);
        if (cancelled) return;
        if (p.estado !== "VALIDO") setIndisponivel(p.estado || "INVALIDO");
        else setPreview(p);
      } catch (err) {
        if (cancelled) return;
        const status = err.response && err.response.status;
        setIndisponivel(status === 429 ? "LIMITE" : "INVALIDO");
      } finally {
        if (!cancelled) setCarregado(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const irParaLogin = useCallback(() => setTimeout(() => history.push(rotaLogin), 1800), [history, rotaLogin]);

  const tratarErro = useCallback(
    (error, titulo) => {
      const data = (error.response && error.response.data) || {};
      const status = error.response && error.response.status;
      let description = data.error || "Não foi possível concluir.";
      if (status === 409 && data.code === "email_em_uso") description = "Este e-mail já está em uso por outra conta. Peça ao embarcador um convite para outro e-mail.";
      else if (status === 409 && data.code === "username_em_uso") description = "Este nome de usuário já está em uso.";
      else if (status === 400 && data.code === "termo_obrigatorio") description = "Para aceitar o convite da Rede Everlog é preciso marcar o aceite do termo.";
      else if (status === 400 && data.code === "termo_versao_invalida") description = "O termo da Rede foi atualizado. Recarregue a página e leia a versão nova.";
      else if (status === 410) description = "Convite expirado, cancelado ou já utilizado.";
      else if (status === 429) description = "Muitas tentativas. Aguarde alguns minutos.";
      addToast({ place: "tr", type: "danger", title: titulo, description, icon: "tim-icons icon-alert-circle-exc" });
      if (status === 410) setIndisponivel("UTILIZADO");
    },
    [addToast],
  );

  const handleSubmit = useCallback(
    async (data) => {
      try {
        if (formRef.current) formRef.current.setErrors({});
        if (!preview.jaTemAcesso) {
          const schema = Yup.object().shape({
            name: Yup.string().required("Nome é obrigatório"),
            userName: Yup.string().required("Nome de usuário é obrigatório"),
            password: Yup.string()
              .required("Senha é obrigatória")
              .min(8, "Mínimo 8 caracteres")
              .test("complexidade", "Use 3 das 4 classes (minúscula, maiúscula, número, símbolo)", (v) => classesPresentes(v || "") >= 3),
            confirmacao: Yup.string()
              .required("Confirme a senha")
              .oneOf([Yup.ref("password")], "As senhas não coincidem"),
          });
          await schema.validate(data, { abortEarly: false });
        }
        if (preview.termo && !termoAceito) {
          addToast({ place: "tr", type: "warning", title: "Aceite do termo", description: "Marque o aceite do termo da Rede Everlog para continuar.", icon: "tim-icons icon-alert-circle-exc" });
          return;
        }
        setSubmitting(true);
        // Convite da Rede: o termo vai junto, com a versão que o preview mostrou.
        const termo = preview.termo ? { termoAceite: true, termoVersao: preview.termo.versao } : {};
        const out = await aceitarConvite(token, preview.jaTemAcesso ? termo : { name: data.name, surname: data.surname || null, userName: data.userName, password: data.password, ...termo });
        addToast({
          place: "tr",
          type: "success",
          title: out.jaTinhaAcesso ? "Embarcador conectado" : out.reativado ? "Acesso reativado" : "Acesso criado",
          description: out.jaTinhaAcesso
            ? "O novo embarcador já aparece no seu painel. Faça login para continuar."
            : "Faça login para continuar a habilitação pelo painel.",
          icon: "tim-icons icon-check-2",
        });
        irParaLogin();
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          if (formRef.current) formRef.current.setErrors(getValidationErrors(error));
          return;
        }
        tratarErro(error, "Erro ao aceitar o convite");
      } finally {
        setSubmitting(false);
      }
    },
    [preview, token, termoAceito, addToast, irParaLogin, tratarErro],
  );

  const recusar = useCallback(async () => {
    setSubmitting(true);
    try {
      await recusarConvite(token, { motivo: motivoRecusa || undefined });
      setRecusado(true);
    } catch (error) {
      tratarErro(error, "Erro ao recusar o convite");
    } finally {
      setSubmitting(false);
      setRecusando(false);
    }
  }, [token, motivoRecusa, tratarErro]);

  const logoContainer = (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(255, 255, 255, 0.92)",
        borderRadius: "16px",
        width: "160px",
        height: "80px",
        marginBottom: "16px",
        boxShadow: "0 4px 20px rgba(0,0,0,0.25)",
      }}
    >
      <img alt="Everlog" src={logo} style={{ width: "140px", objectFit: "contain" }} />
    </div>
  );

  const cartao = (titulo, corpo, rodape) => (
    <div className="content">
      <Container>
        <Col className="ml-auto mr-auto" lg="5" md="7">
          <Card className="glass-card">
            <CardHeader>
              {logoContainer}
              {titulo && <h4>{titulo}</h4>}
            </CardHeader>
            <CardBody>{corpo}</CardBody>
            {rodape && <CardFooter>{rodape}</CardFooter>}
          </Card>
        </Col>
      </Container>
    </div>
  );

  const linkLogin = (
    <div style={{ textAlign: "center" }}>
      <Link to={rotaLogin} style={{ color: "#fff", fontSize: 13 }}>
        Ir para o login
      </Link>
    </div>
  );

  if (!carregado) return cartao(null, <p style={{ ...textoSuave, margin: 0 }}>Carregando…</p>);

  if (recusado) {
    return cartao("Convite recusado", <p style={textoSuave}>Tudo bem. O embarcador foi informado e nada foi criado no portal.</p>, null);
  }

  if (indisponivel) {
    return cartao(
      "Convite indisponível",
      <p style={textoSuave}>{indisponivel === "LIMITE" ? "Muitas tentativas. Aguarde alguns minutos e abra o link de novo." : MENSAGEM_INDISPONIVEL[indisponivel] || MENSAGEM_INDISPONIVEL.INVALIDO}</p>,
      linkLogin,
    );
  }

  const cabecalho = (
    <>
      <h4>
        {preview.termo || preview.rede ? (
          <>
            Convite da <strong>Rede Everlog</strong>
          </>
        ) : (
          <>
            Convite de <strong>{preview.embarcadorNome}</strong>
          </>
        )}
      </h4>
      <div style={{ marginTop: 8, padding: "6px 12px", background: "rgba(29,140,248,0.18)", border: "1px solid rgba(29,140,248,0.4)", borderRadius: 6, fontSize: 13, color: "#fff", textAlign: "center" }}>
        {preview.transportadora ? preview.transportadora.fantasia : "Sua transportadora"} · portal da transportadora Everlog
      </div>
    </>
  );

  if (recusando) {
    return cartao(
      "Recusar o convite",
      <>
        <p style={textoSuave}>Quer contar o motivo? É opcional e ajuda o embarcador.</p>
        <Input type="textarea" rows={3} value={motivoRecusa} onChange={(e) => setMotivoRecusa(e.target.value.slice(0, 500))} placeholder="Motivo (opcional)" />
      </>,
      <div style={{ display: "flex", justifyContent: "space-between", gap: "0.5rem" }}>
        <Button color="secondary" outline size="sm" onClick={() => setRecusando(false)} disabled={submitting}>
          Voltar
        </Button>
        <Button color="danger" size="sm" onClick={recusar} disabled={submitting}>
          Confirmar recusa
        </Button>
      </div>,
    );
  }

  const initialData = {
    email: preview.email,
    userName: (preview.email || "").split("@")[0] || "",
  };

  return (
    <div className="content">
      <Container>
        <Col className="ml-auto mr-auto" lg="5" md="7">
          <Form ref={formRef} onSubmit={handleSubmit} initialData={initialData}>
            <Card className="glass-card">
              <CardHeader>
                {logoContainer}
                {cabecalho}
              </CardHeader>
              <CardBody>
                {preview.jaTemAcesso ? (
                  <p style={textoSuave}>
                    Sua transportadora já tem acesso ao portal. Ao aceitar, <strong>{preview.embarcadorNome}</strong> passa a aparecer no seu painel e a habilitação com ele começa. Nada muda na sua conta.
                  </p>
                ) : (
                  <>
                    <p style={{ ...textoSuave, marginBottom: 14 }}>Crie sua senha de acesso. Depois do login, você conclui a habilitação pelo painel.</p>
                    <Row>
                      <Col md="6">
                        <CustomInputGroupText name="name" placeholder="Nome *" type="text" addonType="prepend" icon="tim-icons icon-single-02" />
                      </Col>
                      <Col md="6">
                        <CustomInputGroupText name="surname" placeholder="Sobrenome" type="text" addonType="prepend" icon="tim-icons icon-single-02" />
                      </Col>
                    </Row>
                    <CustomInputGroupText name="email" placeholder="E-mail" type="email" addonType="prepend" icon="tim-icons icon-email-85" disabled />
                    <CustomInputGroupText name="userName" placeholder="Nome de usuário *" type="text" addonType="prepend" icon="tim-icons icon-badge" />
                    <CustomInputGroupText name="password" placeholder="Senha *" type="password" addonType="prepend" icon="tim-icons icon-lock-circle" />
                    <CustomInputGroupText name="confirmacao" placeholder="Confirme a senha *" type="password" addonType="prepend" icon="tim-icons icon-lock-circle" />
                    <p style={{ ...textoSuave, fontSize: 12, marginBottom: 0 }}>Mínimo 8 caracteres, com 3 das 4 classes: minúscula, maiúscula, número e símbolo.</p>
                  </>
                )}
                {preview.termo ? <TermoRede termo={preview.termo} aceito={termoAceito} onChange={setTermoAceito} disabled={submitting} /> : null}
              </CardBody>
              <CardFooter>
                <Button block color="primary" size="lg" type="submit" disabled={submitting || Boolean(preview.termo && !termoAceito)}>
                  {submitting ? "Enviando…" : "Aceitar convite"}
                </Button>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
                  <Button color="link" size="sm" style={{ color: "rgba(255,255,255,0.7)", paddingLeft: 0 }} onClick={() => setRecusando(true)} disabled={submitting}>
                    Recusar convite
                  </Button>
                  {preview.jaTemAcesso && (
                    <Link to={rotaLogin} style={{ color: "#fff", fontSize: 13 }}>
                      Ir para o login
                    </Link>
                  )}
                </div>
              </CardFooter>
            </Card>
          </Form>
        </Col>
      </Container>
    </div>
  );
}
